import { Injectable } from '@angular/core';

import { BetslipConfig } from '@frontend/sports/common/client-config-data-access';
import { isDefined } from '@frontend/sports/common/core/utils/extended-types';

import { IBetslipState } from '../../../../../base/store/state';
import { isLinear } from '../../../../../base/utils/linear-utils';
import { BetslipType } from '../../../../../core/betslip-type';
import { BetslipPick } from '../../../../../core/picks/betslip-pick';
import { ResultStatus } from '../../../../../core/picks/pick-models';
import { flattenGroupPicksIfAny, flattenPicks, getLegsCount, getPicksCount } from '../../../../../core/utils';
import { ISlip } from '../../../../bet-generation/models';
import { getEditBetResultedState } from '../../../../edit-bet/services/edit-bet-utils';
import { IEditMyBetState } from '../../../../edit-bet/state';
import { filterPicksForType } from '../../../../picks/services/linear-betslip-pick.utils';
import { ISystemBetPickState } from '../../../../system-bet/state';
import { ITeaserBetState } from '../../../../teaser-bet/state';
import {
    BetBuilderSlipValidationContext,
    BetslipTypeValidationContext,
    ComboBetValidationContext,
    EditBetTypeValidationContext,
    EditBetValidationContext,
    SingleBetValidationContext,
    SystemBetValidationContext,
    TeaserBetPickValidationContext,
    TeaserBetValidationContext,
} from '../../../models/models';

@Injectable()
export class TypeContextGeneratorService {
    constructor(private betslipConfig: BetslipConfig) {}

    generateBetslipTypeContext(state: IBetslipState, type: BetslipType): BetslipTypeValidationContext {
        return {
            selectedPicks:
                type === BetslipType.EditBet
                    ? this.getEditBetPicks(state.types.editBet)
                    : filterPicksForType(state.types, type, state.picks.pickList, {
                          isSelected: true,
                          isLocked: isLinear(state) ? false : undefined,
                      }),
            type,
        };
    }

    generateTeaserBetPickContext(teaserBetState: ITeaserBetState): TeaserBetPickValidationContext {
        return {
            picks: teaserBetState.picks,
        };
    }

    generateEditBetContext(editBetState: IEditMyBetState): EditBetValidationContext {
        return {
            addedStake: editBetState.addedStake,
            earlyPayoutStake: editBetState.earlyPayoutStake,
            acceptedEarlyPayoutStake: editBetState.acceptedEarlyPayoutStake,
            activePicksCount: Object.values(editBetState.picks.pickState).filter((p) => !p.isRemoved && p?.resultedState === ResultStatus.Open)
                .length,
            getResultStatus: () => getEditBetResultedState(editBetState),
        };
    }

    generateBetBuilderContext(slip: ISlip): BetBuilderSlipValidationContext {
        const picks = Object.values(slip.slipPicks).map((p) => p.pick);

        return {
            picks,
        };
    }

    generateSingleBetContext(state: IBetslipState): SingleBetValidationContext {
        const isLinearBetslip = isLinear(state);

        const singleStatePicks = flattenGroupPicksIfAny(state.picks.pickList)
            .map((pick) => state.types.singleBet.picks[pick.id.toString()])
            .filter(isDefined);

        return {
            validPicksCount: isLinearBetslip
                ? singleStatePicks.filter((p) => !p.isLocked).length
                : singleStatePicks.filter((p) => p.isSelected).length,
        };
    }

    generateComboBetContext(state: IBetslipState): ComboBetValidationContext {
        const isLinearBetslip = isLinear(state);

        const getPickBetCount = this.betslipConfig.isSportcastAsComboEnabled ? getLegsCount : getPicksCount;

        let picksByType: { validPicks: BetslipPick[]; allPicks: BetslipPick[] } = { validPicks: [], allPicks: [] };

        picksByType = state.picks.pickList.reduce((picks, pick) => {
            const pickState = state.types.comboBet.picks[pick.id.toString()];
            if (pickState) {
                picks.allPicks.push(pick);
                if (pickState.isSelected && (!isLinearBetslip || !pickState.isLocked)) {
                    picks.validPicks.push(pick);
                }
            }

            return picks;
        }, picksByType);

        return {
            totalPicksCount: getPickBetCount(picksByType.allPicks),
            validPicksCount: getPickBetCount(picksByType.validPicks),
            typeContext: this.generateBetslipTypeContext(state, BetslipType.Combo),
        };
    }

    generateSystemBetContext(rawState: IBetslipState): SystemBetValidationContext {
        const isLinearBetslip = isLinear(rawState);
        const state = {
            ...rawState,
            picks: {
                ...rawState.picks,
                pickList: flattenPicks(rawState.picks.pickList),
            },
        };
        const picks = state.picks.pickList.map((p): ISystemBetPickState => state.types.systemBet.picks[p.id.toString()]).filter(isDefined);

        const { validSystemPicksCount, validBankerPicksCount } = picks.reduce(
            (agg, curr) => {
                if (!curr.isSelected || (isLinearBetslip && curr.isLocked)) {
                    return agg;
                }

                if (curr.isBanker) {
                    agg.validBankerPicksCount++;
                } else {
                    agg.validSystemPicksCount++;
                }

                return agg;
            },
            {
                validSystemPicksCount: 0,
                validBankerPicksCount: 0,
            },
        );

        return {
            totalPicksCount: picks.length,
            validSystemPicksCount,
            validBankerPicksCount,
            typeContext: this.generateBetslipTypeContext(state, BetslipType.System),
        };
    }

    generateTeaserBetContext(rawState: IBetslipState): TeaserBetValidationContext {
        const isLinearBetslip = isLinear(rawState);

        const state = {
            ...rawState,
            picks: {
                ...rawState.picks,
                pickList: flattenPicks(rawState.picks.pickList),
            },
        };

        const picks = Object.values(state.types.teaserBet.picks);

        return {
            totalPicksCount: picks.length,
            validPicksCount: isLinearBetslip ? picks.filter((p) => p.isSelected && !p.isLocked).length : picks.filter((p) => p.isSelected).length,
            typeContext: this.generateBetslipTypeContext(state, BetslipType.Teaser),
        };
    }

    generateEditBetTypeContext(state: IBetslipState): EditBetTypeValidationContext {
        return {
            typeContext: this.generateBetslipTypeContext(state, BetslipType.EditBet),
            editBetContext: this.generateEditBetContext(state.types.editBet),
        };
    }

    private getEditBetPicks(editBet: IEditMyBetState): BetslipPick[] {
        const pickState = editBet.picks.pickState;
        const addPickList = editBet.addPicksState.pickList;

        const pickList = editBet.picks.pickList.filter((p) => !pickState[p.id.toString()]?.isRemoved);

        return editBet.addPicksState.isVisible ? [...pickList, ...addPickList] : pickList;
    }
}
